// Mints the short-lived chat ticket the backend-served shell exchanges for an
// identified transcript.
//
// The ticket is opaque and carries no email — never parse it. It lives ~60s, so
// it must be minted immediately before the loader script is injected: respond.io
// renders its chat iframe unconditionally at page load (open/closed is CSS
// only), so the shell loads straight away rather than when the user opens chat.
//
// A lapsed or missing ticket fails closed and silently: the shell renders
// anonymously with an empty transcript and nothing is thrown. The iframe is
// cross-origin, so the parent cannot detect it.
import { getToken } from '~/data/storage';
import { ticketEndpointFromEnv } from './config';

type TicketResponse = { ticket?: unknown };

export async function requestTicket(
  endpoint: string,
  token: string,
): Promise<string | null> {
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      // Auth is the Authorization header, not a cookie. Sending credentials
      // would tighten CORS for no gain.
      credentials: 'omit',
      headers: {
        Authorization: `JWT ${token}`,
      },
    });

    if (!res.ok) {
      return null;
    }

    const body = (await res.json()) as TicketResponse;
    return typeof body?.ticket === 'string' && body.ticket ? body.ticket : null;
  } catch {
    return null;
  }
}

// Returns null for an anonymous visitor: there is no anonymous ticket, and the
// shell deliberately clears respond.io's storage when it renders unidentified so
// the previous user's transcript is not readable by the next person.
export async function mintTicket(): Promise<string | null> {
  const endpoint = ticketEndpointFromEnv();
  if (!endpoint) {
    return null;
  }

  const token = await getToken();
  if (!token) {
    return null;
  }

  return requestTicket(endpoint, token);
}
